import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTheme, usePageTitle, AlertBanner } from '@flowcore-water/ui';
import { globalSearch, type GlobalSearchResult } from '../lib/api';

export function GlobalSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTheme();
  usePageTitle('ST Mirror', 'Search');

  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [data, setData] = useState<GlobalSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(() => {
    if (!q.trim()) { setData(null); return; }
    setLoading(true);
    setError(null);
    globalSearch(q)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { search(); }, [search]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (input.trim()) {
      setSearchParams({ q: input.trim() });
    }
  }

  const resultEntries = data ? Object.entries(data.results) : [];
  const totalMatches = resultEntries.reduce((sum, [, v]) => sum + v.count, 0);

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold tracking-tight" style={{ color: t.textPrimary }}>
        Global Search
      </h1>

      <form onSubmit={handleSubmit} className="mb-6 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search across all entities..."
          className="flex-1 rounded-lg border px-4 py-2 text-sm outline-none"
          style={{ background: t.inputBg, borderColor: t.inputBorder, color: t.textPrimary }}
          autoFocus
        />
        <button
          type="submit"
          className="rounded-lg border px-4 py-2 text-sm font-medium"
          style={{ background: t.buttonBg, borderColor: t.buttonBorder, color: t.buttonText }}
        >
          Search
        </button>
      </form>

      {error && <AlertBanner variant="error" message={error} />}

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <span style={{ color: t.textMuted }}>Searching...</span>
        </div>
      )}

      {data && !loading && (
        <>
          <p className="mb-4 text-sm" style={{ color: t.textMuted }}>
            {totalMatches === 0
              ? `No results for "${data.query}"`
              : `${totalMatches} results across ${resultEntries.length} entities for "${data.query}"`}
          </p>

          <div className="space-y-6">
            {resultEntries.map(([entityKey, group]) => (
              <div key={entityKey}>
                <div className="mb-2 flex items-center gap-2">
                  <h2
                    className="text-xs font-mono uppercase tracking-wide"
                    style={{ color: t.sectionHeading }}
                  >
                    {group.display}
                  </h2>
                  <span
                    className="rounded-full px-2 py-0.5 text-xs font-mono"
                    style={{ background: t.accentBg, color: t.accent }}
                  >
                    {group.count}
                  </span>
                </div>

                <div
                  className="divide-y rounded-lg border"
                  style={{ borderColor: t.borderSubtle, divideColor: t.borderSubtle } as React.CSSProperties}
                >
                  {group.data.map((row, i) => {
                    const label = String(row[group.label_column] || row.id || i);
                    return (
                      <Link
                        key={String(row.id ?? i)}
                        to={`/browse/${entityKey}/${row.id}`}
                        className="flex items-center justify-between px-4 py-3 transition-colors"
                        style={{ borderColor: t.borderSubtle }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = t.surfaceHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div>
                          <span className="text-sm font-medium" style={{ color: t.textPrimary }}>
                            {label}
                          </span>
                          <span className="ml-2 text-xs" style={{ color: t.textMuted }}>
                            #{String(row.id)}
                          </span>
                        </div>
                        <span className="text-xs" style={{ color: t.textMuted }}>→</span>
                      </Link>
                    );
                  })}
                </div>

                {group.count >= 10 && (
                  <Link
                    to={`/browse/${entityKey}?q=${encodeURIComponent(q)}`}
                    className="mt-1 inline-block text-xs"
                    style={{ color: t.accent }}
                  >
                    View all in {group.display} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
